import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAccess } from '@/access/useAccess'
import type { DeskEntry, ExpectedGuest } from '@/api/pending'
import {
  deskBatches,
  deskStatusLabel,
  deskStatusTone,
  deskTitle,
  isWaiting,
  ITEM_LABEL,
  itemsLabel,
  lobbyGuests,
  matchesBatch,
  matchesEntry,
  matchesExpected,
  todayISO,
  carrierBreakdown,
  type Batch,
  oldestSubline,
} from '@/lib/desk'
import { ago, fmtDate } from '@/lib/format'
import {
  Avatar,
  Button,
  Card,
  EmptyRow,
  Icon,
  PageHeader,
  Pill,
  Row,
  SearchInput,
  Segmented,
  Spinner,
  StatTile,
} from '@/ui'
import { cx } from '@/ui/cx'
import { deskListQuery, expectedGuestsQuery } from './api'
import { CodeLookup } from './CodeLookup'
import { DeskIcon } from './DeskIcon'
import styles from './desk.module.css'
import { HandoverModal, type HandoverSubject } from './HandoverModal'
import { RegisterDeliveriesModal } from './RegisterDeliveriesModal'
import { deskStrings as t } from './strings'
import { VisitorModal } from './VisitorModal'

type Tab = 'waiting' | 'expected' | 'history'

type Dialog =
  | { type: 'handover'; subject: HandoverSubject; code?: string }
  | { type: 'items'; prefill?: Batch }
  | { type: 'guest'; prefill?: ExpectedGuest }

export function DeskView() {
  const { condoId, capabilities } = useAccess()
  const canRegister = capabilities.has('desk')
  const waitingQ = useQuery(deskListQuery(condoId, 'waiting'))
  const doneQ = useQuery(deskListQuery(condoId, 'done'))
  const expectedQ = useQuery(expectedGuestsQuery(condoId))

  const [tab, setTab] = useState<Tab>('waiting')
  const [q, setQ] = useState('')
  const [dialog, setDialog] = useState<Dialog | null>(null)

  const loaded = waitingQ.data && doneQ.data && expectedQ.data
  const failed = waitingQ.isError || doneQ.isError || expectedQ.isError

  const header = (
    <PageHeader
      title={t.title}
      subtitle={t.subtitle}
      actions={
        canRegister && (
          <>
            <Button variant="secondary" icon="person" onClick={() => setDialog({ type: 'guest' })}>
              {t.guestArrived}
            </Button>
            <Button icon="move_to_inbox" onClick={() => setDialog({ type: 'items' })}>
              {t.registerDeliveries}
            </Button>
          </>
        )
      }
    />
  )

  if (!loaded) {
    return (
      <div>
        {header}
        {failed ? (
          <Card pad={0} className={styles.screen}>
            <div className={styles.errorState}>
              <EmptyRow icon="error" title={t.loadError} description={t.loadErrorHint} />
              <div className={styles.errorAction}>
                <Button
                  onClick={() => {
                    void waitingQ.refetch()
                    void doneQ.refetch()
                    void expectedQ.refetch()
                  }}
                >
                  {t.retry}
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <div className={styles.loading}>
            <Spinner size={28} />
          </div>
        )}
      </div>
    )
  }

  const waiting = waitingQ.data.entries
  const done = doneQ.data.entries
  const expected = expectedQ.data

  const allBatches = deskBatches(waiting)
  const lobby = lobbyGuests(waiting)
  const itemsWaiting = waiting.filter((e) => e.kind === 'item' && isWaiting(e)).length
  const oldest = allBatches[0]
  const today = todayISO()

  const batches = allBatches.filter((b) => matchesBatch(b, q))
  const lobbyRows = lobby.filter((g) => matchesEntry(g, q))
  const waitingRows: (Batch | DeskEntry)[] = [...batches, ...lobbyRows]
  const historyRows = done.filter((e) => matchesEntry(e, q))
  const expectedRows = expected.filter((g) => matchesExpected(g, q))

  const count =
    tab === 'waiting'
      ? waitingRows.length
      : tab === 'expected'
        ? expectedRows.length
        : historyRows.length
  const empty = t.empty[tab]

  return (
    <div className={styles.screen}>
      {header}

      <div className={styles.stats}>
        <StatTile
          icon="inventory_2"
          label="Itens na portaria"
          value={itemsWaiting}
          meta={`${allBatches.length} ${allBatches.length === 1 ? 'unidade' : 'unidades'} para retirar`}
        />
        <StatTile
          icon="schedule"
          label="Espera mais longa"
          value={oldest ? ago(oldest.oldest_at) : '—'}
          meta={oldest ? `Unidade ${oldest.unit} · ${oldest.resident}` : 'Nada aguardando'}
        />
        <StatTile
          icon="person"
          label="Na portaria"
          value={lobby.length}
          meta="Aguardando entrada"
        />
        <StatTile
          icon="notifications"
          label="Esperados hoje"
          value={expected.filter((g) => g.expected_date === today).length}
          meta="Pré-cadastrados pelos moradores"
        />
      </div>

      {canRegister && (
        <CodeLookup
          condoId={condoId}
          onMatch={(subject, code) => setDialog({ type: 'handover', subject, code })}
        />
      )}

      <div className={styles.toolbar}>
        <Segmented
          aria-label={t.tabsLabel}
          value={tab}
          onChange={setTab}
          items={[
            { id: 'waiting', label: 'Aguardando', count: allBatches.length + lobby.length },
            { id: 'expected', label: 'Esperados', count: expected.length },
            { id: 'history', label: 'Entregues', count: done.length },
          ]}
        />
        <div className={styles.search}>
          <SearchInput value={q} onChange={setQ} placeholder={t.searchPlaceholder} />
        </div>
      </div>

      <Card pad={0} className={styles.table}>
        <Row header>
          <div className={styles.cIcon} />
          <div className={styles.cMain}>{tab === 'expected' ? 'Visitante' : 'Aguardando'}</div>
          <div className={styles.cUnit}>Unidade</div>
          <div className={styles.cWhen}>{tab === 'expected' ? 'Previsto' : 'Registrado'}</div>
          <div className={styles.cStatus}>Status</div>
          <div className={styles.cActions} />
        </Row>

        {tab === 'expected' &&
          expectedRows.map((g) => (
            <Row key={g.id}>
              <div className={styles.cIcon}>
                <Avatar name={g.guest_name} size={40} />
              </div>
              <div className={styles.cMain}>
                <div className={styles.titleLine}>{g.guest_name}</div>
                <div className={styles.subText}>
                  {g.guest_tax_id || 'Sem documento'}
                  {g.booking_id ? ` · reserva #${g.booking_id}` : ''}
                </div>
              </div>
              <div className={styles.cUnit}>
                <div className={styles.primaryText}>
                  {g.unit} · {g.tower}
                </div>
                <div className={styles.subText}>{g.resident}</div>
              </div>
              <div className={cx(styles.cWhen, styles.primaryText)}>
                {g.expected_date === today ? 'Hoje' : fmtDate(g.expected_date)}
              </div>
              <div className={styles.cStatus}>
                <Pill tone="neutral">Esperado</Pill>
              </div>
              <div className={styles.cActions}>
                {canRegister && (
                  <Button size="s" onClick={() => setDialog({ type: 'guest', prefill: g })}>
                    Registrar chegada
                  </Button>
                )}
              </div>
            </Row>
          ))}

        {tab !== 'expected' &&
          (tab === 'waiting' ? waitingRows : historyRows).map((e) => {
            const batch = e.kind === 'batch'
            const waitingRow = batch || isWaiting(e)
            return (
              <Row key={e.id}>
                <div className={styles.cIcon}>
                  <DeskIcon entry={e} />
                </div>
                <div className={styles.cMain}>
                  <div className={styles.titleLine}>
                    {batch ? `${itemsLabel(e.count)} para retirar` : deskTitle(e)}
                    {batch && e.flagged && (
                      <span className={styles.flag}>
                        <Icon name="flag" size={15} />
                      </span>
                    )}
                  </div>
                  <div className={styles.subText}>
                    {batch
                      ? carrierBreakdown(e)
                      : e.kind === 'guest'
                        ? e.guest_tax_id || 'Sem documento'
                        : `${e.carrier} · ${ITEM_LABEL[e.item_type]}`}
                  </div>
                </div>
                <div className={styles.cUnit}>
                  <div className={styles.primaryText}>
                    {e.unit} · {e.tower}
                  </div>
                  <div className={styles.subText}>{e.resident}</div>
                </div>
                <div className={styles.cWhen}>
                  <div className={styles.primaryText}>
                    {ago(batch ? e.newest_at : e.created_at)}
                  </div>
                  <div className={styles.subText}>
                    {batch ? oldestSubline(e) : fmtDate(e.created_at)}
                  </div>
                </div>
                <div className={styles.cStatus}>
                  <Pill tone={deskStatusTone(e)}>{deskStatusLabel(e)}</Pill>
                </div>
                <div className={styles.cActions}>
                  {waitingRow && e.kind !== 'item' ? (
                    <div className={styles.actions}>
                      {batch && canRegister && (
                        <Button
                          size="s"
                          variant="secondary"
                          icon="add"
                          onClick={() => setDialog({ type: 'items', prefill: e })}
                        >
                          Adicionar item
                        </Button>
                      )}
                      <Button
                        size="s"
                        icon="key"
                        onClick={() => setDialog({ type: 'handover', subject: e })}
                      >
                        {e.kind === 'guest' ? 'Confirmar entrada' : 'Entregar tudo'}
                      </Button>
                    </div>
                  ) : (
                    <span className={styles.statusText}>{deskStatusLabel(e)}</span>
                  )}
                </div>
              </Row>
            )
          })}

        {count === 0 && (
          <EmptyRow
            icon={tab === 'waiting' ? 'move_to_inbox' : 'notifications'}
            title={empty.title}
            description={empty.description}
          />
        )}
      </Card>

      {dialog?.type === 'handover' && (
        <HandoverModal
          condoId={condoId}
          subject={dialog.subject}
          verifiedCode={dialog.code}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.type === 'items' && (
        <RegisterDeliveriesModal
          condoId={condoId}
          prefill={dialog.prefill}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.type === 'guest' && (
        <VisitorModal condoId={condoId} prefill={dialog.prefill} onClose={() => setDialog(null)} />
      )}
    </div>
  )
}
